// One line per series over months, up to six.
//
// The markers carry a second fact the line cannot: a SOLID marker is a month
// that has been billed and can never move again, a hollow one is a month that
// still can. A line that mixes the two says which part of itself is settled,
// which is the question somebody comparing two months is really asking.

import type { ReactNode } from "react";
import styles from "./Chart.module.css";
import { axisMax, pointX, showsTick, tickStride, ticks, valueY } from "./scale.js";

export interface TrendPoint {
  readonly period: string;
  readonly value: number;
  /** True when the month has been billed: the figure is what went out. */
  readonly closed: boolean;
}

export interface TrendSeriesLine {
  readonly key: string;
  readonly label: string;
  readonly color: string;
  readonly points: readonly TrendPoint[];
}

export interface TrendLinesProps {
  /** The x axis, stated once: every series carries a point for each of these. */
  readonly periods: readonly string[];
  readonly series: readonly TrendSeriesLine[];
  readonly formatValue: (value: number) => string;
  readonly formatPeriod: (period: string) => string;
  readonly label: string;
  readonly emptyMessage: string;
  /**
   * The plot's height in the same units as its 720-wide box. A panel that
   * fills a window passes its own aspect so the chart fills it too.
   */
  readonly plotHeight?: number;
  /**
   * What a filled and a hollow marker mean. Billing's months are closed as
   * billed or still open. Null for a chart whose periods all mean the same:
   * every marker is filled and the legend names the lines only.
   */
  readonly markerMeaning?: MarkerMeaning | null;
}

export interface MarkerMeaning {
  readonly closed: string;
  readonly open: string;
}

const BILLING_MARKERS: MarkerMeaning = { closed: "closed, as billed", open: "open, priced now" };

const PLOT_WIDTH = 720;
const DEFAULT_PLOT_HEIGHT = 150;
const AXIS_GUTTER = 52;
const MARKER_RADIUS = 4;

/**
 * A month is settled when every line says it is: closedness is a property of the
 * month and the deployment, so the lines cannot honestly disagree — and if one
 * ever did, "open" is the answer that does not claim a figure is final.
 */
function isClosed(series: readonly TrendSeriesLine[], period: string): boolean {
  return series.every((line) =>
    line.points.some((point) => point.period === period && point.closed),
  );
}

export function TrendLines({
  periods,
  series,
  formatValue,
  formatPeriod,
  label,
  emptyMessage,
  markerMeaning = BILLING_MARKERS,
  plotHeight = DEFAULT_PLOT_HEIGHT,
}: TrendLinesProps): ReactNode {
  if (series.length === 0 || periods.length === 0) {
    return <div className={styles.empty}>{emptyMessage}</div>;
  }

  const top = axisMax(
    Math.max(...series.flatMap((line) => line.points.map((point) => point.value))),
  );
  const plotWidth = PLOT_WIDTH - AXIS_GUTTER;
  const x = (index: number): number => AXIS_GUTTER + pointX(index, periods.length, plotWidth);
  const y = (value: number): number => valueY(value, top, plotHeight);
  const stride = tickStride(periods.map(formatPeriod), plotWidth);

  return (
    <figure className={styles.figure}>
      <svg
        className={styles.plot}
        viewBox={`0 0 ${PLOT_WIDTH} ${plotHeight + 18}`}
        role="img"
        aria-label={`${label}. ${series.length} lines over ${periods.length} months. The same figures are in the table below.`}
      >
        {ticks(top).map((tick) => (
          <g key={tick}>
            <line
              className={styles.grid}
              x1={AXIS_GUTTER}
              x2={PLOT_WIDTH}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text className={styles.axisText} x={AXIS_GUTTER - 6} y={y(tick) + 3} textAnchor="end">
              {formatValue(tick)}
            </text>
          </g>
        ))}
        {periods.map((period, index) =>
          !showsTick(index, periods.length, stride) ? null : (
            <text
              key={period}
              className={styles.axisText}
              x={x(index)}
              y={plotHeight + 14}
              // The last label sits on the plot's right edge: centred, half of it
              // would fall outside the figure.
              textAnchor={index === periods.length - 1 && periods.length > 1 ? "end" : "middle"}
            >
              {formatPeriod(period)}
            </text>
          ),
        )}
        {series.map((line) => (
          <g key={line.key}>
            <polyline
              className={styles.line}
              stroke={line.color}
              points={line.points.map((point, index) => `${x(index)},${y(point.value)}`).join(" ")}
            />
            {line.points.map((point, index) => (
              <circle
                key={point.period}
                className={point.closed ? undefined : styles.markerOpen}
                cx={x(index)}
                cy={y(point.value)}
                r={MARKER_RADIUS}
                fill={point.closed ? line.color : undefined}
                stroke={line.color}
              >
                <title>
                  {`${line.label} · ${formatPeriod(point.period)} · ${formatValue(point.value)}${
                    markerMeaning === null
                      ? ""
                      : ` · ${point.closed ? markerMeaning.closed : markerMeaning.open}`
                  }`}
                </title>
              </circle>
            ))}
          </g>
        ))}
      </svg>

      <div className={styles.legend}>
        {series.map((line) => (
          <span key={line.key} className={styles.legendItem}>
            <span className={styles.swatch} style={{ background: line.color }} aria-hidden="true" />
            {line.label}
          </span>
        ))}
        {markerMeaning !== null && (
          <>
            <span className={styles.legendItem}>● {markerMeaning.closed}</span>
            <span className={styles.legendItem}>○ {markerMeaning.open}</span>
          </>
        )}
      </div>

      <div className="srOnly">
        <table>
          <caption>{label}</caption>
          <thead>
            <tr>
              <th scope="col">Series</th>
              {/* The marker's meaning, in words: a reader who cannot see a hollow
                circle still learns which months have been billed. It is stated
                on the column because it is a fact about the month, not about
                any one line. */}
              {periods.map((period) => (
                <th key={period} scope="col">
                  {markerMeaning === null
                    ? formatPeriod(period)
                    : `${formatPeriod(period)} (${isClosed(series, period) ? "closed" : "open"})`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {series.map((line) => (
              <tr key={line.key}>
                <th scope="row">{line.label}</th>
                {line.points.map((point) => (
                  <td key={point.period}>{formatValue(point.value)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
